import { resolveRuntimeEnv } from './resolve';
import type { DeployEnvironment, DeployEnvSettings } from './types';
import { VALID_DEPLOY_ENVS } from './types';

export { envHTML } from './resolve';

/*
 * The env package assumes the following envvars are defined on `process.env` in the runtime:
 * - DEPLOY_ENV
 * - TARGET
 * - COMMIT_HASH
 * - NODE_ENV
 * Currently these are set via webpack.EnvironmentPlugin in each individual build.
 *
 * Do not reference process.env values directly elsewhere in code, use the exports from this module instead!
 */

const { DEPLOY_ENV, overrides = {} } = resolveRuntimeEnv();

if (!VALID_DEPLOY_ENVS.includes(DEPLOY_ENV)) {
  throw new Error(`DEPLOY_ENV must be set to a valid environment (resolved to ${DEPLOY_ENV})`);
}
export { DEPLOY_ENV };

/**
 * Is this a production-style build? When false we may log more verbosely or perform more expensive validations
 */
export const PROD_MODE = process.env.NODE_ENV === 'production';

/**
 * Is this running in a testing context? Use this sparingly
 */
export const IS_TEST = process.env.NODE_ENV === 'test';

/**
 * Is the code being executed in a browser? When false, we are in a server environment like node where
 * browser APIs like `window`, `document`, etc are not available.
 */
export const IS_BROWSER = process.env.TARGET === 'browser';

/**
 * Git commit SHA for frontend. May fall back to timestamp in local development.
 */
export const REVISION = process.env.COMMIT_HASH || (new Date()).toISOString();

const deployEnvVars: Record<DeployEnvironment, DeployEnvSettings> = {
  production: {
    ADYEN_CLIENT_KEY: 'live_PC32TRHVYJHVXKBUFFGLPO2JYIC6O25J',
    ADYEN_ENVIRONMENT: 'live',
    CLOUDINARY_UPLOAD_URL: 'https://api.cloudinary.com/v1_1/reverb/upload',
    CMS_ADMIN_URL: 'https://cms.reverb.tools',
    CORE_API_ORIGIN: 'https://api.reverb.com',
    CORE_WEB_ORIGIN: 'https://reverb.com',
    ECHIDNA_BASE_URL: 'https://echidna.reverb.tools',
    ENABLE_CRITEO: true,
    ENABLE_RUM: false,
    ENABLE_SENTRY: true,
    MPARTICLE_ENV: 'production',
    RQL_BASE_URL: 'https://rql.reverb.com/graphql',
  },
  sandbox: {
    ADYEN_CLIENT_KEY: 'test_47S7SOSMIFB7HJ5XU6IGOSJ77AMKV4EJ',
    ADYEN_ENVIRONMENT: 'test',
    CLOUDINARY_UPLOAD_URL: 'https://api.cloudinary.com/v1_1/reverb/upload',
    CMS_ADMIN_URL: 'https://cms-stable.reverb.tools',
    CORE_API_ORIGIN: 'https://sandbox.reverb.com',
    CORE_WEB_ORIGIN: 'https://sandbox.reverb.com',
    ECHIDNA_BASE_URL: 'https://echidna-staging.reverb.tools',
    ENABLE_CRITEO: false,
    ENABLE_RUM: false,
    ENABLE_SENTRY: false,
    MPARTICLE_ENV: 'staging',
    RQL_BASE_URL: 'https://sandbox.rql.reverb.com/graphql',
  },
  stable: {
    ADYEN_CLIENT_KEY: 'test_47S7SOSMIFB7HJ5XU6IGOSJ77AMKV4EJ',
    ADYEN_ENVIRONMENT: 'test',
    CLOUDINARY_UPLOAD_URL: 'https://api.cloudinary.com/v1_1/reverb/upload',
    CMS_ADMIN_URL: 'https://cms-stable.reverb.tools',
    CORE_API_ORIGIN: 'https://stable.reverb.com',
    CORE_WEB_ORIGIN: 'https://stable.reverb.com',
    ECHIDNA_BASE_URL: 'https://echidna-staging.reverb.tools',
    ENABLE_CRITEO: false,
    ENABLE_RUM: false,
    ENABLE_SENTRY: false,
    MPARTICLE_ENV: 'staging',
    RQL_BASE_URL: 'https://qa.rql.reverb.com/graphql',
  },
  unstable: {
    ADYEN_CLIENT_KEY: 'test_47S7SOSMIFB7HJ5XU6IGOSJ77AMKV4EJ',
    ADYEN_ENVIRONMENT: 'test',
    CLOUDINARY_UPLOAD_URL: 'https://api.cloudinary.com/v1_1/reverb-dev/upload',
    CMS_ADMIN_URL: 'https://cms-unstable.reverb.tools',
    CORE_API_ORIGIN: 'https://unstable.reverb.com',
    CORE_WEB_ORIGIN: 'https://unstable.reverb.com',
    ECHIDNA_BASE_URL: 'https://echidna-staging.reverb.tools',
    ENABLE_CRITEO: true,
    ENABLE_RUM: false,
    ENABLE_SENTRY: false,
    MPARTICLE_ENV: 'staging',
    RQL_BASE_URL: 'https://preprod.rql.reverb.com/graphql',
  },
  tilt: {
    ADYEN_CLIENT_KEY: 'test_47S7SOSMIFB7HJ5XU6IGOSJ77AMKV4EJ',
    ADYEN_ENVIRONMENT: 'test',
    CLOUDINARY_UPLOAD_URL: 'https://api.cloudinary.com/v1_1/reverb-dev/upload',
    CMS_ADMIN_URL: 'https://cms.devo.test',
    CORE_API_ORIGIN: 'https://core.devo.test',
    CORE_WEB_ORIGIN: 'https://core.devo.test',
    ECHIDNA_BASE_URL: 'http://echidna.devo.test',
    ENABLE_CRITEO: false,
    ENABLE_RUM: false,
    ENABLE_SENTRY: false,
    MPARTICLE_ENV: 'staging',
    RQL_BASE_URL: 'https://rql.devo.test/graphql',
  },
  test: {
    ADYEN_CLIENT_KEY: 'test_47S7SOSMIFB7HJ5XU6IGOSJ77AMKV4EJ',
    ADYEN_ENVIRONMENT: 'test',
    CLOUDINARY_UPLOAD_URL: 'https://api.cloudinary.com/v1_1/reverb-dev/upload',
    CMS_ADMIN_URL: 'https://cms.devo.test',
    CORE_API_ORIGIN: 'https://api.test.host:4000',
    CORE_WEB_ORIGIN: 'https://reverb.site:4000',
    ECHIDNA_BASE_URL: 'https://echidna.reverb.test',
    ENABLE_CRITEO: false,
    ENABLE_RUM: false,
    ENABLE_SENTRY: false,
    MPARTICLE_ENV: null,
    RQL_BASE_URL: 'http://localhost:8181/graphql',
  },
};

const configuredEnv = {
  ...deployEnvVars[DEPLOY_ENV],
  ...overrides,
};

export const {
  ADYEN_CLIENT_KEY,
  ADYEN_ENVIRONMENT,
  CLOUDINARY_UPLOAD_URL,
  CMS_ADMIN_URL,
  CORE_API_ORIGIN,
  CORE_WEB_ORIGIN,
  ECHIDNA_BASE_URL,
  ENABLE_CRITEO,
  ENABLE_RUM,
  ENABLE_SENTRY,
  MPARTICLE_ENV,
  RQL_BASE_URL,
} = configuredEnv;
